import QRCode from "qrcode";
import type {
  AppBridge,
  AppBootstrap,
  ClientLogEvent,
  Instruction,
  PaymentProvider,
  SurfaceAction,
  SurfaceBlock,
  SurfaceModel,
  SupportLink,
  Tariff,
} from "@paxvpn-cabinet/domain";

export interface CabinetAppOptions {
  platformLabel: string;
}

type ViewKey = "subscription" | "tariffs" | "instruction" | "support";

interface CabinetState {
  bootstrap: AppBootstrap | null;
  subscriptionSurface: SurfaceModel | null;
  tariffs: Tariff[];
  instruction: Instruction | null;
  support: SupportLink | null;
  activeView: ViewKey;
  loading: boolean;
  error: string | null;
  paymentUrl: string | null;
  qr: QrPreview | null;
}

interface QrPreview {
  title: string;
  value: string;
  dataUrl: string;
}

interface QrPayload {
  title: string;
  value: string;
}

interface CodeBlockParts {
  title: string | null;
  hint: string | null;
  value: string;
}

type ClientLogger = (event: ClientLogEvent) => void;

interface ClipboardApiResult {
  status: "success" | "skipped" | "error";
  errorName?: string;
  errorMessage?: string;
}

interface ExecCommandResult {
  supported: boolean | null;
  result: boolean;
  handlerCalled: boolean;
  handlerSetData: boolean;
  errorName?: string;
  errorMessage?: string;
}

export function createCabinetApp(
  root: HTMLElement,
  bridge: AppBridge,
  options: CabinetAppOptions,
): void {
  const state: CabinetState = {
    bootstrap: null,
    subscriptionSurface: null,
    tariffs: [],
    instruction: null,
    support: null,
    activeView: "subscription",
    loading: true,
    error: null,
    paymentUrl: null,
    qr: null,
  };

  const load = async () => {
    state.loading = true;
    state.error = null;
    render();
    try {
      const bootstrap = await bridge.bootstrap();
      const [subscriptionSurface, tariffs, instruction, support] = await Promise.all([
        bridge.getSurface("subscription"),
        bootstrap.paymentsEnabled ? bridge.getTariffs() : Promise.resolve([]),
        bridge.getInstruction(),
        bridge.getSupportLink(),
      ]);
      state.bootstrap = bootstrap;
      state.subscriptionSurface = subscriptionSurface;
      state.tariffs = tariffs;
      state.instruction = instruction;
      state.support = support;
      if (!bootstrap.paymentsEnabled && state.activeView === "tariffs") {
        state.activeView = "subscription";
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Не удалось загрузить кабинет.";
    } finally {
      state.loading = false;
      render();
    }
  };

  const render = () => {
    root.replaceChildren(
      renderShell(
        state,
        options,
        load,
        setView,
        createPayment,
        dispatchSurfaceAction,
        closeQr,
        logClientEvent,
      ),
    );
  };

  const setView = (view: ViewKey) => {
    state.activeView = view;
    state.paymentUrl = null;
    state.qr = null;
    render();
  };

  const dispatchSurfaceAction = async (action: SurfaceAction) => {
    state.error = null;
    const qr = getQrPayload(action);
    if (qr) {
      try {
        state.qr = {
          title: qr.title,
          value: qr.value,
          dataUrl: await QRCode.toDataURL(qr.value, {
            errorCorrectionLevel: "L",
            margin: 2,
            width: 280,
          }),
        };
      } catch (error) {
        state.error = error instanceof Error ? error.message : "Не удалось сформировать QR-код.";
      }
      render();
      return;
    }

    state.loading = true;
    render();
    try {
      state.subscriptionSurface = await bridge.dispatchAction({
        actionId: action.actionId,
        payload: action.payload,
      });
      state.activeView = "subscription";
      state.qr = null;
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Не удалось выполнить действие.";
    } finally {
      state.loading = false;
      render();
    }
  };

  const closeQr = () => {
    state.qr = null;
    render();
  };

  const logClientEvent: ClientLogger = (event) => {
    void bridge.logClientEvent(event).catch(() => undefined);
  };

  const createPayment = async (planId: string, provider: PaymentProvider) => {
    state.loading = true;
    state.error = null;
    render();
    try {
      const result = await bridge.createPayment({ planId, provider });
      state.paymentUrl = result.paymentUrl;
      state.activeView = "tariffs";
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Не удалось создать ссылку на оплату.";
    } finally {
      state.loading = false;
      render();
    }
  };

  void load();
}

function renderShell(
  state: CabinetState,
  options: CabinetAppOptions,
  reload: () => Promise<void>,
  setView: (view: ViewKey) => void,
  createPayment: (planId: string, provider: PaymentProvider) => Promise<void>,
  dispatchSurfaceAction: (action: SurfaceAction) => Promise<void>,
  closeQr: () => void,
  logClientEvent: ClientLogger,
): HTMLElement {
  const shell = el("main", "cabinet-shell");
  const header = el("header", "cabinet-header");
  header.append(
    el("div", "brand-mark", "PAX"),
    el("div", "brand-copy", [
      el("span", "eyebrow", options.platformLabel),
      el("h1", "", "Кабинет VPN"),
    ]),
    renderStatusPill(state.bootstrap),
  );

  const nav = el("nav", "cabinet-tabs");
  const tabs: Array<[ViewKey, string]> = [
    ["subscription", "🔑 Моя подписка"],
    ...(state.bootstrap?.paymentsEnabled ? ([["tariffs", "🛒 Приобрести доступ"]] as Array<[ViewKey, string]>) : []),
    ["instruction", "📕 Инструкция"],
    ["support", "✍️ Поддержка"],
  ];
  for (const [view, label] of tabs) {
    const button = el("button", view === state.activeView ? "tab active" : "tab", label);
    button.type = "button";
    button.addEventListener("click", () => setView(view));
    nav.append(button);
  }

  const body = el("section", "cabinet-body");
  if (state.loading) {
    body.append(renderLoading());
  } else if (state.error) {
    body.append(renderError(state.error, reload));
  } else if (state.activeView === "subscription") {
    body.append(renderSurface(state.subscriptionSurface, dispatchSurfaceAction, logClientEvent));
  } else if (state.activeView === "tariffs" && state.bootstrap?.paymentsEnabled) {
    body.append(renderTariffs(state.tariffs, state.paymentUrl, createPayment));
  } else if (state.activeView === "instruction") {
    body.append(renderInstruction(state.instruction));
  } else {
    body.append(renderSupport(state.support));
  }

  shell.append(header, nav, body);
  if (state.qr) {
    shell.append(renderQrPreview(state.qr, closeQr));
  }
  return shell;
}

function renderStatusPill(bootstrap: AppBootstrap | null): HTMLElement {
  const label = bootstrap ? `v${bootstrap.protocolVersion} · ${bootstrap.appVersion}` : "загрузка";
  return el("span", "status-pill", label);
}

function renderLoading(): HTMLElement {
  const panel = el("div", "panel");
  panel.append(
    el("div", "skeleton wide"),
    el("div", "skeleton"),
    el("div", "skeleton short"),
  );
  return panel;
}

function renderError(error: string, reload: () => Promise<void>): HTMLElement {
  const panel = el("div", "panel error-panel");
  const button = el("button", "primary-button", "Повторить");
  button.type = "button";
  button.addEventListener("click", () => void reload());
  panel.append(el("h2", "", "Не удалось загрузить данные"), el("p", "", error), button);
  return panel;
}

function renderSurface(
  surface: SurfaceModel | null,
  dispatchSurfaceAction: (action: SurfaceAction) => Promise<void>,
  logClientEvent: ClientLogger,
): HTMLElement {
  const panel = el("div", "panel surface-panel");
  if (!surface) {
    panel.append(el("p", "", "Нет данных подписки."));
    return panel;
  }

  if (surface.title) {
    panel.append(el("h2", "", plainText(surface.title)));
  }
  for (const notice of surface.notices) {
    panel.append(el("p", "notice success", plainText(notice)));
  }
  for (const block of surface.blocks) {
    panel.append(renderSurfaceBlock(block, logClientEvent));
  }
  if (surface.actions.length > 0) {
    const actions = el("div", "surface-actions");
    for (const action of surface.actions) {
      actions.append(renderSurfaceAction(action, dispatchSurfaceAction));
    }
    panel.append(actions);
  }
  return panel;
}

function renderSurfaceBlock(
  block: SurfaceBlock,
  logClientEvent: ClientLogger,
): HTMLElement {
  const wrapper = el("section", `surface-block ${block.kind}`);
  if (block.title) {
    wrapper.append(el("h3", "", plainText(block.title)));
  }
  if (block.kind === "list") {
    const list = el("ul", "surface-list");
    for (const item of block.items) {
      list.append(el("li", "", plainText(item)));
    }
    wrapper.append(list);
    return wrapper;
  }
  if (block.body) {
    const text = plainText(block.body);
    wrapper.append(
      block.kind === "code"
        ? renderCodeBlock(text, logClientEvent)
        : el("p", "surface-text", text),
    );
  }
  return wrapper;
}

function renderCodeBlock(
  text: string,
  logClientEvent: ClientLogger,
): HTMLElement {
  const parts = parseCodeBlock(text);
  const wrapper = el("div", "surface-code");
  if (parts.title) {
    wrapper.append(el("h3", "surface-code-title", parts.title));
  }
  if (parts.hint) {
    wrapper.append(el("p", "surface-code-hint", parts.hint));
  }

  const row = el("div", "code-copy-row");
  const code = renderCopyableCode(parts.value);
  const copy = el("button", "copy-button", "Копировать");
  copy.type = "button";
  copy.addEventListener("click", () => {
    void copyToClipboard(parts.value, code, copy, logClientEvent);
  });
  row.append(code, copy);
  wrapper.append(row);
  return wrapper;
}

function renderCopyableCode(value: string): HTMLElement {
  const code = document.createElement("pre");
  code.className = "access-box code-value";
  code.textContent = value;
  return code;
}

function parseCodeBlock(text: string): CodeBlockParts {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) {
    return { title: null, hint: null, value: "" };
  }
  if (paragraphs.length === 1) {
    return { title: null, hint: null, value: paragraphs[0] };
  }

  const [title, ...rest] = paragraphs;
  const value = rest.pop() ?? "";
  return {
    title: title.replace(/:$/, ""),
    hint: rest.length > 0 ? rest.join("\n\n") : null,
    value,
  };
}

async function copyToClipboard(
  value: string,
  code: HTMLElement,
  button: HTMLButtonElement,
  logClientEvent: ClientLogger,
): Promise<void> {
  const telegramPlatform = window.Telegram?.WebApp?.platform;

  const permission = await getClipboardWritePermission();
  const apiResult = await copyToClipboardWithApi(value);
  const execResult = apiResult.status === "success" ? emptyExecCommandResult() : copyWithClipboardEvent(value);
  logClientEvent({
    event: "clipboard.copy_attempt",
    details: {
      apiErrorMessage: apiResult.errorMessage,
      apiErrorName: apiResult.errorName,
      apiResult: apiResult.status,
      clipboardWritePermission: permission,
      clipboardApiAvailable: Boolean(navigator.clipboard?.writeText),
      codeElementTag: code.tagName.toLowerCase(),
      documentFocus: document.hasFocus(),
      eventPhase: "click",
      execCommandHandlerCalled: execResult.handlerCalled,
      execCommandResult: execResult.result,
      execCommandSetData: execResult.handlerSetData,
      execCommandSupported: execResult.supported,
      isSecureContext: window.isSecureContext,
      permissionsApiAvailable: Boolean(navigator.permissions?.query),
      selectedLength: value.length,
      telegramPlatform,
      telegramVersion: window.Telegram?.WebApp?.version,
      userAgent: navigator.userAgent,
      valueKind: classifyCopyValue(value),
      valueLength: value.length,
      visibilityState: document.visibilityState,
    },
  });
  setTemporaryButtonLabel(
    button,
    apiResult.status === "success" || (execResult.result && execResult.handlerSetData)
      ? "Скопировано"
      : "Ошибка",
  );
}

async function copyToClipboardWithApi(value: string): Promise<ClipboardApiResult> {
  try {
    if (!navigator.clipboard?.writeText) {
      return { status: "skipped" };
    }
    await navigator.clipboard.writeText(value);
    return { status: "success" };
  } catch (error) {
    return {
      status: "error",
      errorName: error instanceof Error ? error.name : "unknown",
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }
}

function copyWithClipboardEvent(value: string): ExecCommandResult {
  const supported = document.queryCommandSupported ? document.queryCommandSupported("copy") : null;
  let handlerCalled = false;
  let handlerSetData = false;
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.readOnly = true;
  textarea.setAttribute("aria-hidden", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  const handleCopy = (event: ClipboardEvent) => {
    handlerCalled = true;
    if (!event.clipboardData) {
      return;
    }
    event.clipboardData.setData("text/plain", value);
    event.preventDefault();
    handlerSetData = true;
  };
  document.addEventListener("copy", handleCopy, true);
  try {
    document.body.append(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    return {
      supported,
      result: document.execCommand("copy"),
      handlerCalled,
      handlerSetData,
    };
  } catch (error) {
    return {
      supported,
      result: false,
      handlerCalled,
      handlerSetData,
      errorName: error instanceof Error ? error.name : "unknown",
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  } finally {
    document.removeEventListener("copy", handleCopy, true);
    textarea.remove();
  }
}

function emptyExecCommandResult(): ExecCommandResult {
  return {
    supported: document.queryCommandSupported ? document.queryCommandSupported("copy") : null,
    result: false,
    handlerCalled: false,
    handlerSetData: false,
  };
}

async function getClipboardWritePermission(): Promise<string> {
  try {
    if (!navigator.permissions?.query) {
      return "unsupported";
    }
    const permission = await navigator.permissions.query({ name: "clipboard-write" as PermissionName });
    return permission.state;
  } catch (error) {
    return error instanceof Error ? `error:${error.name}` : "error:unknown";
  }
}

function classifyCopyValue(value: string): string {
  if (value.startsWith("vless://")) {
    return "vless";
  }
  if (value.startsWith("https://")) {
    return "https";
  }
  if (value.startsWith("http://")) {
    return "http";
  }
  return "other";
}

function setTemporaryButtonLabel(button: HTMLButtonElement, label: string): void {
  const previous = button.textContent ?? "Копировать";
  button.textContent = label;
  button.classList.toggle("copied", label === "Скопировано");
  button.classList.toggle("copy-error", label === "Ошибка");
  button.classList.toggle("selected", label === "Выделено");
  window.setTimeout(() => {
    button.textContent = previous;
    button.classList.remove("copied", "copy-error", "selected");
  }, 1400);
}

function renderSurfaceAction(
  action: SurfaceAction,
  dispatchSurfaceAction: (action: SurfaceAction) => Promise<void>,
): HTMLElement {
  const url = typeof action.payload.url === "string" ? action.payload.url : null;
  if (action.kind === "open_url" && url) {
    const link = el("a", "surface-action", action.label);
    link.href = url;
    link.target = "_blank";
    link.rel = "noreferrer";
    return link;
  }

  const button = el("button", "surface-action", action.label);
  button.type = "button";
  button.addEventListener("click", () => void dispatchSurfaceAction(action));
  return button;
}

function renderQrPreview(qr: QrPreview, closeQr: () => void): HTMLElement {
  const backdrop = el("div", "qr-backdrop");
  const modal = el("div", "qr-modal");
  const image = document.createElement("img");
  image.src = qr.dataUrl;
  image.alt = qr.title;
  const close = el("button", "primary-button", "Закрыть");
  close.type = "button";
  close.addEventListener("click", closeQr);
  modal.append(el("h2", "", qr.title), image, el("pre", "access-box qr-value", qr.value), close);
  backdrop.append(modal);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) {
      closeQr();
    }
  });
  return backdrop;
}

function renderTariffs(
  tariffs: Tariff[],
  paymentUrl: string | null,
  createPayment: (planId: string, provider: PaymentProvider) => Promise<void>,
): HTMLElement {
  const panel = el("div", "panel");
  panel.append(el("h2", "", "Приобрести доступ"));
  if (paymentUrl) {
    const link = el("a", "payment-link", "Открыть оплату");
    link.href = paymentUrl;
    link.target = "_blank";
    link.rel = "noreferrer";
    panel.append(el("p", "notice", "Ссылка на оплату создана."), link);
  }
  const list = el("div", "tariff-list");
  for (const tariff of tariffs) {
    const card = el("article", "tariff-card");
    card.append(
      el("div", "tariff-title", tariff.title),
      el("div", "tariff-meta", `${tariff.description} · ${tariff.priceLabel}`),
    );
    const actions = el("div", "provider-row");
    for (const provider of tariff.availableProviders) {
      const button = el("button", "provider-button", providerLabel(provider));
      button.type = "button";
      button.addEventListener("click", () => void createPayment(tariff.id, provider));
      actions.append(button);
    }
    card.append(actions);
    list.append(card);
  }
  panel.append(list);
  return panel;
}

function renderInstruction(instruction: Instruction | null): HTMLElement {
  const panel = el("div", "panel text-panel");
  panel.append(el("h2", "", instruction?.title ?? "Инструкция"));
  for (const block of instruction?.blocks ?? []) {
    if (block.title) {
      panel.append(el("h3", "", block.title));
    }
    panel.append(el("p", "", block.body));
  }
  return panel;
}

function renderSupport(support: SupportLink | null): HTMLElement {
  const panel = el("div", "panel");
  panel.append(el("h2", "", "Поддержка"), el("p", "", "Оператор поддержки доступен по ссылке."));
  if (support) {
    const link = el("a", "primary-link", support.label);
    link.href = support.url;
    link.target = "_blank";
    link.rel = "noreferrer";
    panel.append(link);
  }
  return panel;
}

function getQrPayload(action: SurfaceAction): QrPayload | null {
  const qr = action.payload.qr;
  if (!qr || typeof qr !== "object") {
    return null;
  }
  const candidate = qr as Partial<QrPayload>;
  if (typeof candidate.value !== "string" || !candidate.value) {
    return null;
  }
  return {
    title: typeof candidate.title === "string" && candidate.title ? candidate.title : action.label,
    value: candidate.value,
  };
}

function plainText(value: string): string {
  const withoutTags = value.replace(/<[^>]*>/g, "");
  const textarea = document.createElement("textarea");
  textarea.innerHTML = withoutTags;
  return textarea.value;
}

function providerLabel(provider: PaymentProvider): string {
  return provider === "platega" ? "Platega" : "Cryptomus";
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  content?: string | HTMLElement[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (typeof content === "string") {
    node.textContent = content;
  } else if (Array.isArray(content)) {
    node.append(...content);
  }
  return node;
}
